"use client";

import React from "react";
import { useErrorLogger } from "./ErrorLoggerProvider";

export interface AppErrorBoundaryProps {
  label?: string;
  children: React.ReactNode;
  /**
   * A static ReactNode or a render function that receives the error and a reset
   * callback to clear the boundary state.
   */
  fallback?:
    | React.ReactNode
    | ((info: { error: any; reset: () => void }) => React.ReactNode);
  silent?: boolean;
}

interface AppErrorBoundaryState {
  error?: any;
}

export class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { error: undefined };
  static getDerivedStateFromError(error: any): AppErrorBoundaryState {
    return { error };
  }
  componentDidCatch(error: any, info: any) {
    document.dispatchEvent(
      new CustomEvent("__APP_ERROR__", {
        detail: { error, info, label: this.props.label },
      })
    );
  }
  private reset = () => this.setState({ error: undefined });

  render() {
    if (this.state.error && !this.props.silent) {
      if (this.props.fallback) {
        return typeof this.props.fallback === "function"
          ? (this.props.fallback as any)({
              error: this.state.error,
              reset: this.reset,
            })
          : this.props.fallback;
      }
      return null;
    }
    return this.props.children;
  }
}

export const ErrorBoundary: React.FC<AppErrorBoundaryProps> = (props) => {
  const { addLog, getTags } = useErrorLogger();
  React.useEffect(() => {
    const handler = (e: Event) => {
      const detail: any = (e as CustomEvent).detail;
      if (!detail) return;
      const { error, info, label } = detail;
      addLog({
        message: error?.message || String(error),
        stack: error?.stack,
        componentStack: info?.componentStack,
        tags: { boundary: label },
        meta: { tags: getTags() },
      });
    };
    document.addEventListener("__APP_ERROR__", handler);
    return () => document.removeEventListener("__APP_ERROR__", handler);
  }, [addLog, getTags]);
  return <AppErrorBoundary {...props} />;
};
